import React, { useState } from "react";
import { CButton, CSpinner, CTooltip } from "@coreui/react-pro";
import { useNavigate } from "react-router-dom";
import { createMotorPredefined } from "../../utils/backend";
import { Formik, Form, Field, ErrorMessage, FieldArray } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import CIcon from "@coreui/icons-react";
import { cilTrash } from "@coreui/icons";

const insuranceProviders = [
  "AIA GENERAL BERHAD",
  "AIG MALAYSIA INSURANCE BERHAD",
  "ALLIANZ GENERAL INSURANCE COMPANY (MALAYSIA) BERHAD",
  "BERJAYA SOMPO INSURANCE BERHAD",
  "CHUBB INSURANCE MALAYSIA BERHAD",
  "ETIQA GENERAL INSURANCE BERHAD",
  "ETIQA GENERAL TAKAFUL BERHAD",
  "GENERALI INSURANCE BERHAD",
  "GREAT EASTERN GENERAL INSURANCE (MALAYSIA) BERHAD",
  "LIBERTY GENERAL INSURANCE BERHAD",
  "LONPAC INSURANCE BERHAD",
  "MSIG INSURANCE (MALAYSIA) BERHAD",
  "PACIFIC AND ORIENT INSURANCE COMPANY BERHAD",
  "QBE INSURANCE (MALAYSIA) BERHAD",
  "RHB INSURANCE BERHAD",
  "SYARIKAT TAKAFUL MALAYSIA AM BERHAD",
  "TAKAFUL IKHLAS GENERAL BERHAD",
  "THE PACIFIC INSURANCE BERHAD",
  "TOKIO MARINE INSURANCE (MALAYSIA) BERHAD",
  "TUNE INSURANCE MALAYSIA BERHAD",
  "ZURICH GENERAL INSURANCE MALAYSIA BERHAD",
  "ZURICH GENERAL TAKAFUL MALAYSIA BERHAD",
];

const CreateMotorProduct = () => {
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);

  const navigate = useNavigate();

  const urlRegex =
    /^((https?):\/\/)?(www\.)?[a-z0-9-]+(\.[a-z0-9-]+)+([/?].*)?$/i;

  const initialValues = {
    planName: "",
    insuranceName: "",
    vehicleType: "",
    policyWordingURL: "",
    productDisclosureURL: "",
    planStatus: "",
    planBenefit: [""],
    add_ons: [{ add_on_name: "", price: "0.00" }],
  };

  const validationSchema = Yup.object().shape({
    planName: Yup.string().required("Plan Name is required"),
    insuranceName: Yup.string().required("Insurance Name is required"),
    vehicleType: Yup.string().required("Vehicle Type is required"),
    policyWordingURL: Yup.string()
      .matches(urlRegex, "Enter a valid URL")
      .required("Policy Wording Document is required"),
    productDisclosureURL: Yup.string()
      .matches(urlRegex, "Enter a valid URL")
      .required("Product Disclosure Sheet Document is required"),
    planBenefit: Yup.array().of(
      Yup.string()
        .required("Plan Benefit is required")
        .max(120, "Benefit must be 120 characters or less")
    ),
  });

  const handleSubmit = async (
    values,
    { setSubmitting, setErrors, setFieldTouched }
  ) => {
    setIsSubmittingForm(true);
    try {
      const filteredAddOns = values.add_ons.filter(
        (item) =>
          item.add_on_name?.toString().trim() !== "" ||
          item.price?.toString().trim() !== ""
      );

      await createMotorPredefined({
        plan_name: values.planName,
        insurance_name: values.insuranceName,
        veh_type: values.vehicleType,
        policy_wording_url: values.policyWordingURL,
        product_disclosure_sheet_url: values.productDisclosureURL,
        plan_status: true,
        plan_benefits: values.planBenefit,
        add_ons: filteredAddOns,
      });
      toast.success("Created predefined motor insurance successfully!");
      navigate("/motor-insurance");
    } catch (error) {
      console.error("Failed to create predefined motor insurance:", error);
      toast.error(
        error.message ||
          "Failed to create predefined motor insurance. Please try again."
      );
      setErrors({
        submit: "Failed to create predefined motor insurance",
        ...error.fieldErrors,
      });
      Object.keys(values).forEach((field) =>
        setFieldTouched(field, true, false)
      );
    } finally {
      setSubmitting(false);
      setIsSubmittingForm(false);
    }
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {({ setFieldValue, isSubmitting, errors, touched }) => (
          <Form>
            {isSubmittingForm && (
              <div className="form-overlay">
                <CSpinner color="primary" />
              </div>
            )}
            <div className="card border-light">
              <div className="card-body">
                {errors.submit && (
                  <div className="alert alert-danger">{errors.submit}</div>
                )}
                <div className="row">
                  <div className="col-12 mb-3">
                    <label htmlFor="planName" className="form-label">
                      <span className="text-danger">*</span> Plan Name
                    </label>{" "}
                    <Field
                      name="planName"
                      as="input"
                      type="text"
                      placeholder="Enter Plan Name"
                      className={`form-control ${
                        touched.planName && errors.planName ? "is-invalid" : ""
                      }`}
                    />
                    <ErrorMessage
                      name="planName"
                      component="div"
                      className="invalid-feedback"
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label htmlFor="insuranceName">
                      <span className="text-danger">*</span> Insurance Name
                    </label>
                    <Field
                      name="insuranceName"
                      as="select"
                      className={`form-control ${
                        touched.insuranceName && errors.insuranceName
                          ? "is-invalid"
                          : ""
                      }`}
                      onChange={(e) => {
                        setFieldValue("insuranceName", e.target.value);
                      }}
                    >
                      <option value="">Select Insurance Name</option>
                      {insuranceProviders.map((provider, index) => (
                        <option key={index} value={provider}>
                          {provider}
                        </option>
                      ))}
                    </Field>
                    {touched.insurance && errors.insurance && (
                      <div className="invalid-feedback d-block mt-1">
                        {errors.insurance}
                      </div>
                    )}
                  </div>

                  <div className="col-md-6 mb-3">
                    <label htmlFor="vehicleType" className="form-label">
                      <span className="text-danger">*</span> Vehicle Type
                    </label>{" "}
                    <div className="input-group">
                      <Field
                        name="vehicleType"
                        as="select"
                        className={`form-select ${
                          touched.vehicleType && errors.vehicleType
                            ? "is-invalid"
                            : ""
                        }`}
                      >
                        <option value="">Select Vehicle Type</option>
                        <option value="private car">Private Car</option>
                        <option value="private motorcycle">
                          Private Motor
                        </option>
                        <option value="company car">Company Car</option>
                        <option value="company motorcycle">
                          Company Motor
                        </option>
                      </Field>
                      <ErrorMessage
                        name="vehicleType"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                  </div>

                  <div className="col-md-6 mb-3">
                    <label htmlFor="policyWordingURL" className="form-label">
                      <span className="text-danger">*</span> Upload Policy
                      Wording Document (PDF)
                    </label>{" "}
                    <Field
                      name="policyWordingURL"
                      as="input"
                      type="text"
                      placeholder="Enter URL Link"
                      className={`form-control ${
                        touched.policyWordingURL && errors.policyWordingURL
                          ? "is-invalid"
                          : ""
                      }`}
                    />
                    <ErrorMessage
                      name="policyWordingURL"
                      component="div"
                      className="invalid-feedback"
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label
                      htmlFor="productDisclosureURL"
                      className="form-label"
                    >
                      <span className="text-danger">*</span> Upload Product
                      Disclosure Sheet Document (PDF)
                    </label>{" "}
                    <Field
                      name="productDisclosureURL"
                      as="input"
                      type="text"
                      placeholder="Enter URL Link"
                      className={`form-control ${
                        touched.productDisclosureURL &&
                        errors.productDisclosureURL
                          ? "is-invalid"
                          : ""
                      }`}
                    />
                    <ErrorMessage
                      name="productDisclosureURL"
                      component="div"
                      className="invalid-feedback"
                    />
                  </div>

                  <div className="row">
                    <div className="col-md-12 mb-3">
                      <span className="text-danger">*</span>{" "}
                      <label htmlFor="planBenefit" className="form-label">
                        Plan Benefits
                        <CTooltip content="Add up to 10 benefits">
                          <span className="ms-2" style={{ marginLeft: "5px" }}>
                            <i className="bi bi-info-circle"></i>
                          </span>
                        </CTooltip>
                      </label>
                      <FieldArray name="planBenefit">
                        {({ push, remove, form }) => (
                          <>
                            {form.values.planBenefit.map((benefit, index) => (
                              <div key={index} className="mb-2">
                                <div className="position-relative">
                                  <input
                                    id={`planBenefit.${index}`}
                                    name={`planBenefit.${index}`}
                                    type="text"
                                    placeholder="Enter Benefit"
                                    maxLength={120}
                                    className={`form-control pe-5 ${
                                      touched.planBenefit?.[index] &&
                                      errors.planBenefit?.[index]
                                        ? "is-invalid"
                                        : ""
                                    }`}
                                    onChange={(e) => {
                                      setFieldValue(
                                        `planBenefit.${index}`,
                                        e.target.value
                                      );
                                    }}
                                    value={benefit}
                                  />
                                  <div
                                    className="position-absolute top-50 end-0 translate-middle-y d-flex align-items-center"
                                    style={{
                                      gap: "8px",
                                      marginRight: "1em", // Default margin
                                      ...(touched.planBenefit?.[index] &&
                                      errors.planBenefit?.[index]
                                        ? { marginRight: "1.9rem" }
                                        : {}),
                                    }}
                                  >
                                    <span className="text-muted">
                                      {benefit.length}/120
                                    </span>
                                    {form.values.planBenefit.length > 1 && (
                                      <CButton
                                        className="btn btn-sm btn-danger text-white"
                                        style={{
                                          padding: "2px 6px",
                                          fontSize: "0.75rem",
                                        }}
                                        title="Delete"
                                        onClick={() => {
                                          remove(index);
                                        }}
                                      >
                                        <i className=" bi bi-trash-fill"></i>
                                      </CButton>
                                    )}
                                  </div>
                                </div>

                                {touched.planBenefit?.[index] &&
                                  errors.planBenefit?.[index] && (
                                    <div className="invalid-feedback d-block mt-1">
                                      {errors.planBenefit[index]}
                                    </div>
                                  )}
                              </div>
                            ))}
                            {form.values.planBenefit.length > 0 &&
                              form.values.planBenefit.length < 10 && (
                                <button
                                  type="button"
                                  className="btn d-flex align-items-center"
                                  style={{
                                    backgroundColor: "transparent",
                                    color: "#0B4FE4",
                                  }}
                                  onClick={() => push("")}
                                >
                                  <span className="me-2">+</span> Add Benefit
                                </button>
                              )}
                          </>
                        )}
                      </FieldArray>
                      <ErrorMessage
                        name="planBenefit"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-12 mb-3">
                      <label htmlFor="add_ons" className="form-label">
                        Add-ons
                      </label>
                      <FieldArray name="add_ons">
                        {({ push, remove, form }) => (
                          <>
                            {form.values.add_ons.length === 0 && (
                              <div className="d-flex justify-content-start mb-2">
                                <button
                                  type="button"
                                  className="btn d-flex align-items-center"
                                  style={{
                                    backgroundColor: "transparent",
                                    color: "#0B4FE4",
                                  }}
                                  onClick={() =>
                                    push({ add_on_name: "", price: "" })
                                  }
                                >
                                  <span className="me-2">+</span> Add Add-ons
                                </button>
                              </div>
                            )}
                            {form.values.add_ons?.map((addOn, index) => (
                              <div key={index} className="mb-2">
                                <div className="d-flex">
                                  <div
                                    className="flex-grow-2 me-2 position-relative"
                                    style={{ flex: "2 1 0" }}
                                  >
                                    <input
                                      id={`add_ons.${index}.add_on_name`}
                                      name={`add_ons.${index}.add_on_name`}
                                      type="text"
                                      placeholder="Enter Name"
                                      maxLength={50}
                                      className={`form-control pe-5 ${
                                        form.touched.add_ons?.[index]
                                          ?.add_on_name &&
                                        form.errors.add_ons?.[index]
                                          ?.add_on_name
                                          ? "is-invalid"
                                          : ""
                                      }`}
                                      onChange={(e) => {
                                        setFieldValue(
                                          `add_ons.${index}.add_on_name`,
                                          e.target.value
                                        );
                                      }}
                                      value={addOn.add_on_name || ""} // Ensure value is a string
                                    />
                                    <div
                                      className="position-absolute top-50 end-0 translate-middle-y"
                                      style={{
                                        color: "#6c757d",
                                        marginRight: "1em", // Default margin
                                        ...(form.touched.add_ons?.[index]
                                          ?.name &&
                                        form.errors.add_ons?.[index]
                                          ?.add_on_name
                                          ? { marginRight: "1.9rem" }
                                          : {}),
                                      }}
                                    >
                                      {addOn.add_on_name?.length}/50
                                    </div>
                                  </div>
                                  {/* <div
                                    className="flex-grow-1 position-relative"
                                    style={{ flex: "1 1 0" }}
                                  >
                                    <input
                                      id={`add_ons.${index}.price`}
                                      name={`add_ons.${index}.price`}
                                      type="text"
                                      placeholder="Enter Price"
                                      className={`form-control ps-5 ${
                                        form.touched.add_ons?.[index]?.price &&
                                        form.errors.add_ons?.[index]?.price
                                          ? "is-invalid"
                                          : ""
                                      }`}
                                      onChange={(e) => {
                                        setFieldValue(
                                          `add_ons.${index}.price`,
                                          e.target.value
                                        );
                                      }}
                                    />
                                    <span
                                      className="position-absolute top-50 start-0 translate-middle-y ms-2"
                                      style={{ color: "#6c757d" }}
                                    >
                                      RM
                                    </span>
                                  </div> */}
                                  {form.values.add_ons.length > 1 && (
                                    <CButton
                                      className="btn btn-sm btn-danger text-white ms-2"
                                      title="Delete"
                                      onClick={() => {
                                        remove(index);
                                      }}
                                    >
                                      <i className=" bi bi-trash-fill"></i>
                                    </CButton>
                                  )}
                                </div>
                                {form.touched.add_ons?.[index]?.add_on_name &&
                                form.errors.add_ons?.[index]?.add_on_name ? (
                                  <div className="invalid-feedback d-block mt-1">
                                    Add-ons is required
                                  </div>
                                ) : null}
                              </div>
                            ))}
                            {form.values.add_ons?.length > 0 && (
                              <button
                                type="button"
                                className="btn d-flex align-items-center"
                                style={{
                                  backgroundColor: "transparent",
                                  color: "#0B4FE4",
                                }}
                                onClick={() =>
                                  push({ add_on_name: "", price: "0.00" })
                                } // Push a new object with default values
                              >
                                <span className="me-2">+</span> Add More
                              </button>
                            )}
                          </>
                        )}
                      </FieldArray>
                    </div>
                  </div>
                </div>

                <div className=" d-flex justify-content-end">
                  <button
                    type="button"
                    className="btn me-3 ansa-button-secondary"
                    onClick={() => navigate("/motor-insurance")}
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    className="btn ansa-button-primary"
                    disabled={isSubmitting}
                  >
                    Create
                  </button>
                </div>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default CreateMotorProduct;
